import React, { ChangeEvent } from 'react';
import { Panel, PanelLayout } from '../../../../components/PanelLayout';
import { FormControl, Form } from 'components/Form';
import Toggle from 'components/Toggle/Toggle';
import FormattedInputNestedLabel from 'components/FormattedInputNestedLabel/FormattedInputNestedLabel';
import styles from './ServerFlowSettingsPanel.module.scss';
import StatusIndicator from 'components/StatusIndicator/StatusIndicator';
import { useAppDispatch } from 'redux/store';
import { Controller, Control, FieldErrors, useFieldArray, useWatch, UseFormSetValue } from 'react-hook-form';
import { setFormIsDirty } from 'redux/actions/uiActions/formStateActions';
import { ILocationServerFlow, PosSystemType } from '@ready/dashboardv2api.contracts';

const LOW_AUTH_AMOUNT_CENTS = 100;

type Props = {
  control: Control<ServerFlowSettingsPanelType>;
  setValue: UseFormSetValue<ServerFlowSettingsPanelType>;
  isSaving: boolean;
  errors: FieldErrors<ServerFlowSettingsPanelType>;
  defaultValues?: Partial<ServerFlowSettingsPanelType>;
  posType?: PosSystemType;
};

const supportedPosTypes = [PosSystemType.ncraloha, PosSystemType.omnivore];
const isUsingSupportedPos = (posType: PosSystemType) => supportedPosTypes.includes(posType);

export type ServerFlowSettingsPanelType = Pick<ILocationServerFlow, 'chatAssistantFeatureEnabled' | 'customAiPrompt' | 'inactivityLockEnabled' | 'screenTimeoutDurationInMinutes' | 'serverFlowFeatureEnabled' | 'feedbackEnabled' | 'allowManualKeyEntry' | 'requireEmployeePasswordForManualKeyEntry' | 'securedTabEnabled' | 'securedTabAuthAmount'> & {
  cannedPrompts?: Array<{ title: string; prompt: string }>;
};

export const ServerFlowSettingsPanel = (props: Props) => {
  const { isSaving, defaultValues, control, setValue, errors, posType = PosSystemType.readypos } = props;
  const dispatch = useAppDispatch();
  const { fields, append, remove } = useFieldArray({ control, name: 'cannedPrompts' });
  const securedTabEnabledValue = useWatch({ control, name: 'securedTabEnabled' });

  return (
    <PanelLayout>
      <Panel title='ServerFlow' headerControls={null}>
        <Form hasGroups>
          <FormControl
            label='Status'
            labelTooltip="ServerFlow must be configured by Support. If you're interested in this functionality please reach out."
          >
            <StatusIndicator
              active={defaultValues?.serverFlowFeatureEnabled ?? false}
              activeLabel='Enabled'
              inactiveLabel='Disabled'
            />
          </FormControl>

          {isUsingSupportedPos(posType) && (
            <FormControl
              label='Lock Screen when inactive'
              additionalStyles={styles.toggleContainer}
              labelNoteComponent={
                <span className={styles.labelNote}>
                  The ServerFlow screen will lock after a selected period of inactivity. Pins used to access the app
                  come from your POS, please check with support to see if your POS supports this functionality before
                  enabling.
                </span>
              }
              disabled={isSaving}
            >
              <Controller
                name='inactivityLockEnabled'
                control={control}
                render={({ field, field: { value, onChange } }) => (
                  <Toggle
                    {...field}
                    additionalStyles={styles.toggle}
                    checked={value}
                    loading={isSaving}
                    onChange={(checked) => {
                      onChange(checked);
                      dispatch(setFormIsDirty(true));
                    }}
                  />
                )}
              />
            </FormControl>
          )}

          {isUsingSupportedPos(posType) && (
            <FormControl label='Screen Timeouts after' disabled={isSaving}>
              <div className={styles.screenTimeoutContainer}>
                <Controller
                  name='screenTimeoutDurationInMinutes'
                  control={control}
                  rules={{
                    validate: (value) => {
                      const parsedValue = parseFloat(value);
                      if (isNaN(parsedValue)) {
                        return 'Must be a valid number';
                      }
                      if (parsedValue < 0) {
                        return 'Must be a positive number';
                      }
                      return true;
                    },
                  }}
                  render={({ field: { value, onChange } }) => (
                    <FormattedInputNestedLabel
                      format='min'
                      label=''
                      value={value}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        onChange(e.target.value.toString());
                        dispatch(setFormIsDirty(true));
                      }}
                      withError={!!errors?.screenTimeoutDurationInMinutes?.message}
                      errorMessage={errors?.screenTimeoutDurationInMinutes?.message}
                      loading={isSaving}
                    />
                  )}
                />
              </div>
            </FormControl>
          )}

          <FormControl
            label='Use Chat Assistant'
            additionalStyles={styles.toggleContainer}
            labelNoteComponent={
              <span className={styles.labelNote}>
                This will enable chat for the guest app and allow users to request refills or ask to speak to their
                server. Notifications are displayed in the ServerFlow App.
              </span>
            }
            disabled={isSaving}
          >
            <Controller
              name='chatAssistantFeatureEnabled'
              control={control}
              render={({ field: { value, onChange } }) => (
                <Toggle
                  additionalStyles={styles.toggle}
                  checked={value}
                  onChange={(checked) => {
                    onChange(checked);
                    dispatch(setFormIsDirty(true));
                  }}
                  disabled={isSaving}
                />
              )}
            />
          </FormControl>

          <Controller
            name='customAiPrompt'
            control={control}
            render={({ field: { value, onChange } }) => (
              <FormControl
                label='Custom AI Prompt'
                labelNoteComponent={
                  <span className={styles.labelNote}>
                    Customize the instructions given to the AI chat assistant. Leave blank to use the default prompt.
                  </span>
                }
                disabled={isSaving}
              >
                <textarea
                  className={styles.customAiPromptTextarea}
                  value={value ?? ''}
                  onChange={(e) => {
                    onChange(e.target.value);
                    dispatch(setFormIsDirty(true));
                  }}
                  placeholder='Enter custom AI prompt instructions...'
                  rows={8}
                  disabled={isSaving}
                />
              </FormControl>
            )}
          />

          <FormControl
            label='Canned Prompts'
            labelNoteComponent={
              <span className={styles.labelNote}>
                Configure starter prompts that appear in the chat assistant. Each prompt has a title shown to the user and
                the actual prompt text sent when clicked.
              </span>
            }
            disabled={isSaving}
          >
            <div className={styles.cannedPromptsContainer}>
              {fields.map((field, index) => (
                <div key={field.id} className={styles.cannedPromptRow}>
                  <Controller
                    name={`cannedPrompts.${index}.title`}
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <input
                        className={styles.cannedPromptInput}
                        value={value ?? ''}
                        onChange={(e) => {
                          onChange(e.target.value);
                          dispatch(setFormIsDirty(true));
                        }}
                        placeholder='Title (shown to user)'
                        disabled={isSaving}
                      />
                    )}
                  />
                  <Controller
                    name={`cannedPrompts.${index}.prompt`}
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <input
                        className={styles.cannedPromptInput}
                        value={value ?? ''}
                        onChange={(e) => {
                          onChange(e.target.value);
                          dispatch(setFormIsDirty(true));
                        }}
                        placeholder='Prompt text (sent as message)'
                        disabled={isSaving}
                      />
                    )}
                  />
                  <button
                    type='button'
                    className={styles.cannedPromptRemoveButton}
                    onClick={() => {
                      remove(index);
                      dispatch(setFormIsDirty(true));
                    }}
                    disabled={isSaving}
                  >
                    Remove
                  </button>
                </div>
              ))}
              <button
                type='button'
                className={styles.cannedPromptAddButton}
                onClick={() => {
                  append({ title: '', prompt: '' });
                  dispatch(setFormIsDirty(true));
                }}
                disabled={isSaving}
              >
                + Add Prompt
              </button>
            </div>
          </FormControl>

          <FormControl
            label='Enable Feedback'
            additionalStyles={styles.toggleContainer}
            labelNoteComponent={
              <span className={styles.labelNote}>
                This will enable the feedback prompt on the tip screen, allowing customers to share their experience in
                the ServerFlow app.
              </span>
            }
            disabled={isSaving}
          >
            <Controller
              name='feedbackEnabled'
              control={control}
              render={({ field: { value, onChange } }) => (
                <Toggle
                  additionalStyles={styles.toggle}
                  checked={value}
                  onChange={(checked) => {
                    onChange(checked);
                    dispatch(setFormIsDirty(true));
                  }}
                  disabled={isSaving}
                />
              )}
            />
          </FormControl>
          <FormControl
            label='Enable Secured Tabs'
            additionalStyles={styles.toggleContainer}
            labelNoteComponent={
              <span className={styles.labelNote}>
                When enabled, servers can open secured tabs (pre-authorized card holds) for tables. The secured tab UI
                will appear in the ServerFlow app.
              </span>
            }
            disabled={isSaving}
          >
            <Controller
              name='securedTabEnabled'
              control={control}
              render={({ field: { value, onChange } }) => (
                <Toggle
                  additionalStyles={styles.toggle}
                  checked={value}
                  onChange={(checked) => {
                    onChange(checked);
                    if (!checked) setValue('securedTabAuthAmount', 0);
                    dispatch(setFormIsDirty(true));
                  }}
                  disabled={isSaving}
                />
              )}
            />
          </FormControl>
          {securedTabEnabledValue && (
            <FormControl
              label='Low Authorization Secured Tabs'
              additionalStyles={styles.toggleContainer}
              labelNoteComponent={
                <span className={styles.labelNote}>
                  When enabled, secured tabs will pre-authorize $1 instead of the full check total.
                  The authorization will not increment as the check grows. The full amount is still
                  captured at close.  There is no assurance that the full amount can be captured at close.
                </span>
              }
              disabled={isSaving}
            >
              <Controller
                name='securedTabAuthAmount'
                control={control}
                render={({ field: { value, onChange } }) => (
                  <Toggle
                    additionalStyles={styles.toggle}
                    checked={value === LOW_AUTH_AMOUNT_CENTS}
                    onChange={(checked) => {
                      onChange(checked ? LOW_AUTH_AMOUNT_CENTS : 0);
                      dispatch(setFormIsDirty(true));
                    }}
                    disabled={isSaving}
                  />
                )}
              />
            </FormControl>
          )}
          <FormControl
            label='Allow Manual Key Entry'
            additionalStyles={styles.toggleContainer}
            disabled={isSaving}
          >
            <Controller
              name='allowManualKeyEntry'
              control={control}
              render={({ field: { value, onChange } }) => (
                <Toggle
                  checked={value}
                  onChange={(checked) => {
                    onChange(checked);
                    dispatch(setFormIsDirty(true));
                  }}
                />
              )}
            />
          </FormControl>
          <FormControl
            label='Require Employee Password for Manual Key Entry'
            additionalStyles={styles.toggleContainer}
            disabled={isSaving}
          >
            <Controller
              name='requireEmployeePasswordForManualKeyEntry'
              control={control}
              render={({ field: { value, onChange } }) => (
                <Toggle
                  checked={value}
                  onChange={(checked) => {
                    onChange(checked);
                    dispatch(setFormIsDirty(true));
                  }}
                />
              )}
            />
          </FormControl>
        </Form>
      </Panel>
    </PanelLayout>
  );
};
